/**
 * theme.js — core utilities shared by all theme JS.
 * Ports of: components/viewport-height-fix.tsx, components/animations/*,
 * lib/utils helpers, Radix Sheet/Dialog open-close behavior (as generic
 * data-drawer / data-modal controllers) and the scroll-to-top button.
 *
 * Loaded with defer; the critical --vh first-paint value is set by an inline
 * one-liner in theme.liquid, and re-managed here on resize.
 */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ */
  /* Viewport height fix (port of viewport-height-fix.tsx)               */
  /* Sets --vh so .h-fixed-screen/.min-h-fixed-screen are stable on iOS. */
  /* ------------------------------------------------------------------ */
  function setVh() {
    document.documentElement.style.setProperty(
      "--vh",
      window.innerHeight * 0.01 + "px",
    );
  }
  setVh();
  window.addEventListener("resize", setVh);
  window.addEventListener("orientationchange", setVh);

  /* ------------------------------------------------------------------ */
  /* Small helpers                                                       */
  /* ------------------------------------------------------------------ */
  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Shopify money formatter. `cents` is an integer amount in subunits,
   * `format` defaults to the shop money format injected in theme.liquid.
   */
  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    const moneyFormat =
      format || (window.themeConfig && window.themeConfig.moneyFormat) || "€{{amount}}";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ",";
      decimal = decimal || ".";
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : "";
      return dollars + centsPart;
    }

    let value = "";
    switch (moneyFormat.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return moneyFormat.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------ */
  /* Scroll-triggered animations                                         */
  /* Port of components/animations/scroll-reveal.tsx + landing patterns: */
  /*  - .animate-on-scroll[-left|-right]  -> gets .visible               */
  /*  - [data-animate-in-view]            -> gets .animate-in-view       */
  /* Re-run via themeUtils.refreshAnimations() after section re-renders. */
  /* ------------------------------------------------------------------ */
  const seen = new WeakSet();
  const io =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const el = entry.target;
              if (el.hasAttribute("data-animate-in-view")) {
                el.classList.add("animate-in-view");
              } else {
                el.classList.add("visible");
              }
              io.unobserve(el);
            }
          },
          { threshold: 0.15, rootMargin: "0px 0px -40px 0px" },
        )
      : null;

  function refreshAnimations(root) {
    const scope = root || document;
    const targets = scope.querySelectorAll(
      ".animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right, [data-animate-in-view]",
    );
    targets.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      if (io) {
        io.observe(el);
      } else {
        el.classList.add(
          el.hasAttribute("data-animate-in-view") ? "animate-in-view" : "visible",
        );
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Generic drawer controller (replaces Radix Sheet)                    */
  /* Markup contract:                                                    */
  /*   <div data-drawer id="CartDrawer">                                 */
  /*     <div class="drawer-overlay" data-drawer-overlay></div>          */
  /*     <div class="drawer-panel">…<button data-drawer-close>…</div>    */
  /*   </div>                                                            */
  /* Triggers anywhere: <button data-drawer-open="CartDrawer">           */
  /* ------------------------------------------------------------------ */
  function setDrawer(id, open) {
    const root = document.getElementById(id);
    if (!root) return;
    root.setAttribute("data-open", open ? "true" : "false");
    // The body state flag intentionally uses a DIFFERENT attribute name than
    // the [data-drawer-open] *trigger* attribute. The click handler detects
    // open-triggers via closest("[data-drawer-open]"), and <body> is an
    // ancestor of every drawer — reusing that name made every click inside an
    // open drawer (X button, overlay, outside click) re-open it instead of
    // closing it.
    if (open) {
      document.body.setAttribute("data-drawer-active", id);
    } else if (document.body.getAttribute("data-drawer-active") === id) {
      document.body.removeAttribute("data-drawer-active");
    }
    root.dispatchEvent(
      new CustomEvent(open ? "drawer:open" : "drawer:close", { bubbles: true }),
    );
  }

  /* Generic modal controller (replaces Radix Dialog).
     Markup: <div data-modal id="X"><div class="modal-overlay" data-modal-overlay>
             <div role="dialog">…<button data-modal-close>…</div></div></div>
     Trigger: <button data-modal-open="X"> */
  function setModal(id, open) {
    const root = document.getElementById(id);
    if (!root) return;
    root.setAttribute("data-open", open ? "true" : "false");
    root.dispatchEvent(
      new CustomEvent(open ? "modal:open" : "modal:close", { bubbles: true }),
    );
  }

  document.addEventListener("click", (e) => {
    const openDrawer = e.target.closest("[data-drawer-open]");
    if (openDrawer) {
      e.preventDefault();
      setDrawer(openDrawer.getAttribute("data-drawer-open"), true);
      return;
    }
    const closeDrawer = e.target.closest("[data-drawer-close], [data-drawer-overlay]");
    if (closeDrawer) {
      const root = closeDrawer.closest("[data-drawer]");
      if (root) setDrawer(root.id, false);
      return;
    }
    const openModal = e.target.closest("[data-modal-open]");
    if (openModal) {
      e.preventDefault();
      setModal(openModal.getAttribute("data-modal-open"), true);
      return;
    }
    const closeModal = e.target.closest("[data-modal-close]");
    if (closeModal) {
      const root = closeModal.closest("[data-modal]");
      if (root) setModal(root.id, false);
      return;
    }
    const overlay = e.target.matches("[data-modal-overlay]") ? e.target : null;
    if (overlay) {
      const root = overlay.closest("[data-modal]");
      if (root) setModal(root.id, false);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const openDrawerId = document.body.getAttribute("data-drawer-active");
    if (openDrawerId) setDrawer(openDrawerId, false);
    document
      .querySelectorAll('[data-modal][data-open="true"]')
      .forEach((m) => setModal(m.id, false));
  });

  /* ------------------------------------------------------------------ */
  /* Scroll-to-top button (port of scroll-to-top-button.tsx)             */
  /* ------------------------------------------------------------------ */
  function initScrollTop() {
    const btn = document.querySelector("[data-scroll-top]");
    if (!btn) return;
    const toggle = () => {
      if (window.scrollY > 300) {
        btn.classList.remove("opacity-0", "pointer-events-none");
      } else {
        btn.classList.add("opacity-0", "pointer-events-none");
      }
    };
    window.addEventListener("scroll", toggle, { passive: true });
    toggle();
    btn.addEventListener("click", () =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */
  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(() => {
    refreshAnimations();
    initScrollTop();
  });

  /* ------------------------------------------------------------------ */
  /* Theme editor: re-initialize a section after it is re-rendered.      */
  /* `shopify:section:load` fires ONLY inside the theme editor (never on  */
  /* the live storefront), so this whole block is a no-op for real        */
  /* visitors. Without it, an edited section's fresh DOM is never         */
  /* re-revealed or re-bound: .animate-on-scroll cards keep their initial */
  /* opacity:0 (the section looks BLANK) and carousels / video / FAQ      */
  /* accordions / maps stop responding. Each module is poked through the  */
  /* rescan hook it already exposes; missing hooks are skipped safely.    */
  /* ------------------------------------------------------------------ */
  function revealNow(root) {
    root
      .querySelectorAll(
        ".animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right",
      )
      .forEach((el) => el.classList.add("visible"));
    root
      .querySelectorAll("[data-animate-in-view]")
      .forEach((el) => el.classList.add("animate-in-view"));
  }

  function reinitSection(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;
    // Force-reveal immediately (rather than re-observing) so content is
    // guaranteed visible regardless of the edited section's scroll position.
    revealNow(root);
    const hooks = [
      () => window.themeCarousels && window.themeCarousels.scan(root),
      () => window.themeVideos && window.themeVideos.scan(root),
      () => window.themeVideos && window.themeVideos.initClickToPlay(root),
      () => window.themeCampaign && window.themeCampaign.scan(root),
      () => window.themeProductExtras && window.themeProductExtras.scan(root),
      () => window.themePages && window.themePages.scan(root),
      () => window.themeMaps && window.themeMaps.scan(root),
      () => window.themeLanding && window.themeLanding.scan(root),
      () => window.themeShop && window.themeShop.scan(root),
      () => window.themeProduct && window.themeProduct.reinit(root),
      () => window.themeHeader && window.themeHeader.reinit(root),
    ];
    hooks.forEach((run) => {
      try {
        run();
      } catch (e) {
        /* one module failing must not abort the rest */
      }
    });
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", (e) =>
      reinitSection(e.target),
    );
  }

  window.themeUtils = {
    debounce,
    formatMoney,
    onReady,
    refreshAnimations,
    openDrawer: (id) => setDrawer(id, true),
    closeDrawer: (id) => setDrawer(id, false),
    openModal: (id) => setModal(id, true),
    closeModal: (id) => setModal(id, false),
  };
})();
